import React, { useState } from 'react';

import { routes } from '@aline/routes';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import { getPreLoginRedirectURI } from 'src/auth';
import { AlineButton, AlineTooltip } from 'src/components';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { RequestDocAccessImg } from 'src/components/icons/RequestDocAccess';
import { homePageRoute } from 'src/config';
import { getOnEnterHandler, openLink } from 'src/helpers';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { useRedirectPath } from './hooks';

const useStyles = makeStyles()(theme => ({
  button: {
    width: 'fit-content',
  },
  card: {
    ['@media (max-width: 600px) and (orientation: portrait)']: {
      gridTemplateColumns: '1fr',
      padding: theme.spacing(2),
      width: '100%',
    },
    ['@media (max-width: 900px) and (orientation: landscape)']: {
      position: 'relative',
    },
    background: theme.palette.common.white,
    borderRadius: 20,
    boxShadow: '0px 12px 30px 0px rgba(50, 74, 82, 0.15)',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr 176px',
    padding: theme.spacing(5),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: 324,
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    justifyContent: 'center',
  },
  imageGridItem: {
    ['@media (max-width: 600px) and (orientation: portrait)']: {
      display: 'none',
    },
  },
  infoBox: {
    padding: theme.spacing(2, 0),
  },
  requestAccessImg: {
    ['@media (max-width: 900px) and (orientation: landscape)']: {
      left: 'unset',
      right: 30,
      width: 232,
    },
    left: 44,
    position: 'relative',
  },
  root: {
    ['@media (max-width: 600px) and (orientation: portrait)']: {
      padding: theme.spacing(0, 2),
    },
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
    width: '100%',
  },
  wrapper: {
    ['@media (max-width: 600px) and (orientation: portrait)']: {
      width: '100%',
    },
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface PreLoginProps {
  accessRequestedOverride: AccessRequestedProps | null;
}

export function PreLogin({ accessRequestedOverride }: PreLoginProps): JSX.Element {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.card}>
          <div className={classes.gridItem}>
            <AlineLogo />
            <AccessCheckBody accessRequestedOverride={accessRequestedOverride} />
          </div>
          <div className={cx(classes.gridItem, classes.imageGridItem)}>
            <RequestDocAccessImg className={classes.requestAccessImg} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessCheckBody({ accessRequestedOverride }: PreLoginProps): JSX.Element {
  const [accessRequested, setAccessRequested] = useState<AccessRequestedProps | null>(null);
  const accessRequestedProps = accessRequestedOverride ?? accessRequested;

  if (accessRequestedProps) return <AccessRequested {...accessRequestedProps} />;

  return <AccessCheck setAccessRequested={setAccessRequested} />;
}

interface AccessCheckProps {
  setAccessRequested: ReactSetStateAction<AccessRequestedProps | null>;
}

function AccessCheck({ setAccessRequested }: AccessCheckProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { redirectPath } = useRedirectPath();
  const [email, setEmail] = useState<string>('');
  const [requestingAccess, setRequestingAccess] = useState<boolean>(false);
  const isValidEmail = z.string().trim().email().safeParse(email).success;

  async function handleCheckAccess() {
    if (!email) return;

    setRequestingAccess(true);
    const { redirectUri } = await getPreLoginRedirectURI(
      email.trim(),
      redirectPath ?? routes.DashboardIndex
    );
    if (!redirectUri) {
      setAccessRequested({
        msg: 'An error occurred processing your request.  Try again.',
        title: 'Error',
      });
      return setRequestingAccess(false);
    }

    if (!redirectUri.pageLoad) {
      navigate(redirectUri.route);
      return setRequestingAccess(false);
    }

    setAccessRequested({
      msg: 'You have an account and must login to access the requested resource.',
      title: 'Redirecting to login...',
    });
    setRequestingAccess(false);
    window.location.href = redirectUri.route;
  }

  function scrollInputIntoView(e: React.FocusEvent<HTMLInputElement>) {
    if (!isMobile) return;
    const input = e.currentTarget;
    function scrollIntoView() {
      input.scrollIntoView({ block: 'center' });
    }

    // Scroll now too, in case the keyboard is already open (a re-focus).
    scrollIntoView();

    const viewport = window.visualViewport;
    if (!viewport) return;
    viewport.addEventListener('resize', scrollIntoView);
    input.addEventListener('blur', () => viewport.removeEventListener('resize', scrollIntoView), {
      once: true,
    });
  }

  return (
    <div className={classes.container}>
      <div>
        <Typography variant="h1">Enter Email to Access</Typography>
        <Typography>
          Enter your email below to check your access to the requested resource.
        </Typography>
      </div>
      <AlineInput
        autoFocus
        /**
         * On mobile, bump the input font to 16px so iOS Safari doesn't auto-zoom
         * on focus (it zooms when a focused input's font-size is < 16px).
         */
        inputProps={isMobile ? { style: { fontSize: 16 } } : undefined}
        placeholder="Enter your email..."
        value={email}
        onChange={e => setEmail(e.target.value)}
        onFocus={scrollInputIntoView}
        onKeyDown={getOnEnterHandler(handleCheckAccess)}
      />
      <AlineTooltip arrow placement="top" title={!isValidEmail ? 'Enter a valid email' : ''}>
        <div className={classes.button}>
          <AlineButton
            color="primary"
            disabled={!isValidEmail}
            loading={requestingAccess}
            variant="contained"
            onClick={handleCheckAccess}
          >
            Check Access
          </AlineButton>
        </div>
      </AlineTooltip>
    </div>
  );
}

interface AccessRequestedProps {
  msg: string;
  title: string;
}

function AccessRequested({ msg, title }: AccessRequestedProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.infoBox}>
        <Typography variant="h1">{title}</Typography>
        <Typography>{msg}</Typography>
      </div>
      <AlineButton color="primary" variant="outlined" onClick={() => openLink(homePageRoute)}>
        Go to Aline
      </AlineButton>
    </div>
  );
}
